import { MIRA_SECTIONS, MiraBranchDocument, MiraSection, MiraSectionDocument } from "@/data/models/Mira";
import {
  getClientRef,
  getClientRefById,
  getMemberClientIds,
  hasAdministrationCapabilities,
} from "@/data/repository/ClientRepository";
import { db } from "@/main";

/** The group document id for a section, written on the master only (`group_dataQuality`, …). */
function groupDocId(section: MiraSection): string {
  return `group_${section}`;
}

/**
 * A Mira document frozen at its top level, so Vue 2 does not make it reactive (2026-11-03). Vue walks every
 * nested value of an object assigned to a component's data and installs a getter and setter on each; with the
 * months, quarters and per-entity documents (49 periods, up to 280 KiB) that slowed every Mira screen. A
 * frozen object is skipped whole, its periods and figures included. Mira only reads these documents, and
 * each is replaced as a whole, never changed in place.
 */
export function frozenMiraDocument<T extends object>(document: T): T {
  return Object.freeze(document);
}

/** The branch comparison document id for a section (`branches_devices`, …). */
function branchDocId(section: MiraSection): string {
  return `branches_${section}`;
}

/**
 * Whether the signed-in client is a group master: it administrates a group and has member clients.
 * Only a master carries the group documents (`clients/{master}/mira/group_{section}`) and may read its
 * members' own documents, so this gates the comparison. A member client, or a master visited through
 * the member switch, is not one (its `memberClientIds` is empty), so the comparison never offers there.
 */
export function isMiraGroupMaster(): boolean {
  return hasAdministrationCapabilities() && getMemberClientIds().length > 0;
}

/**
 * Reads every Mira section document of the signed-in client, in parallel.
 *
 * One read per section, once per visit: the documents change only when the nightly job runs, so
 * a listener would cost a connection for nothing. A section the job wrote nothing for (a client with
 * no insurance offers, or one Mira excludes) is simply absent from the result.
 *
 * On a **group master** the group documents (`group_{section}`, computed across the group) are read
 * instead, and a section without a group document falls back to the master's own plain `{section}`
 * document, so a master with figures of its own still sees them.
 *
 * @returns the documents that exist, keyed by section.
 */
export async function loadMiraSections(): Promise<Partial<Record<MiraSection, MiraSectionDocument>>> {
  const collection = db.doc(getClientRef()).collection("mira");
  const master = isMiraGroupMaster();
  const snapshots = await Promise.all(
    MIRA_SECTIONS.map((section) => collection.doc(master ? groupDocId(section) : section).get())
  );

  const sections: Partial<Record<MiraSection, MiraSectionDocument>> = {};
  const missingGroupSections: MiraSection[] = [];
  snapshots.forEach((snapshot, index) => {
    if (snapshot.exists) {
      sections[MIRA_SECTIONS[index]] = frozenMiraDocument(snapshot.data() as MiraSectionDocument);
    } else if (master) {
      missingGroupSections.push(MIRA_SECTIONS[index]);
    }
  });

  // A master without a group document for a section still reads its own plain document (it may have
  // own data), so the screen is not blank where the group figure was not computed.
  if (missingGroupSections.length > 0) {
    const fallbacks = await Promise.all(missingGroupSections.map((section) => collection.doc(section).get()));
    fallbacks.forEach((snapshot, index) => {
      if (snapshot.exists) {
        sections[missingGroupSections[index]] = frozenMiraDocument(snapshot.data() as MiraSectionDocument);
      }
    });
  }

  return sections;
}

/**
 * A small per-session cache of member section documents, keyed `${memberId}:${section}`. The documents
 * change nightly, so caching them for the session is safe and spares a re-read when the user moves
 * between Mira screens or toggles the comparison off and on. A plain object, not a `Map`: Vue 2 cannot
 * track `Map` mutations, and this is read from reactive getters.
 */
const memberSectionCache: Record<string, MiraSectionDocument | null> = {};

/**
 * Reads one member client's own section document (`clients/{memberId}/mira/{section}`), for the
 * comparison. A master may read these under the `isGroupMember` Firestore rule.
 *
 * Returns `null` when the member has no such document, and **swallows a read error per member on
 * purpose**: one unreadable member must not blank the whole comparison card, it just drops out of it
 * (into the "Ohne Wert" line). Cached for the session.
 */
export async function loadMemberSection(
  memberId: string,
  section: MiraSection
): Promise<MiraSectionDocument | null> {
  const cacheKey = `${memberId}:${section}`;
  if (cacheKey in memberSectionCache) {
    return memberSectionCache[cacheKey];
  }

  try {
    const snapshot = await db.doc(getClientRefById(memberId)).collection("mira").doc(section).get();
    const document = snapshot.exists ? frozenMiraDocument(snapshot.data() as MiraSectionDocument) : null;
    memberSectionCache[cacheKey] = document;
    return document;
  } catch (error) {
    // One member we cannot read drops out of the comparison rather than failing the whole card.
    memberSectionCache[cacheKey] = null;
    return null;
  }
}

/**
 * Reads one Mandant's section document as IRES Cloud copies it into a company's viewing client
 * (`clients/{viewer}/mira/companyMember_{section}_{clientId}`, #2610112 X6e). The viewing client is no master
 * of the company's Mandanten, so it cannot read them as a master reads its members; their documents come
 * into its own `mira` instead. `null` where there is none, a read error swallowed as for a member. Cached for
 * the session in the member cache, under its own key.
 */
export async function loadCompanyMemberSection(memberId: string, section: MiraSection): Promise<MiraSectionDocument | null> {
  const cacheKey = `company:${memberId}:${section}`;
  if (cacheKey in memberSectionCache) {
    return memberSectionCache[cacheKey];
  }

  try {
    const snapshot = await db.doc(getClientRef()).collection("mira").doc(`companyMember_${section}_${memberId}`).get();
    const document = snapshot.exists ? frozenMiraDocument(snapshot.data() as MiraSectionDocument) : null;
    memberSectionCache[cacheKey] = document;
    return document;
  } catch (error) {
    // One Mandant we cannot read drops out of the comparison rather than failing the whole card.
    memberSectionCache[cacheKey] = null;
    return null;
  }
}

/** The two Markt entities IRES Cloud publishes per section (#2610072): every project once, every Mandant equally. */
export type MiraMarketEntity = "projects" | "clients";

/** A per-session cache of the Markt documents, keyed `${entity}:${section}`; they change nightly. */
const marketSectionCache: Record<string, MiraSectionDocument | null> = {};

/**
 * Reads one Markt document (`miraMarket/{entity}_{section}`, Track #2610072): the section's figures over
 * every published Mandant, built by IRES Cloud like a group's, readable by any signed-in user. Returns
 * `null` where there is none, and swallows a read error, so the "Markt" option simply does not appear
 * rather than failing the screen. Cached for the session.
 */
export async function loadMarketSection(entity: MiraMarketEntity, section: MiraSection): Promise<MiraSectionDocument | null> {
  const cacheKey = `${entity}:${section}`;
  if (cacheKey in marketSectionCache) {
    return marketSectionCache[cacheKey];
  }

  try {
    const snapshot = await db.collection("miraMarket").doc(`${entity}_${section}`).get();
    const document = snapshot.exists ? frozenMiraDocument(snapshot.data() as MiraSectionDocument) : null;
    marketSectionCache[cacheKey] = document;
    return document;
  } catch (error) {
    // No Markt rather than a failed screen; the next visit tries again.
    return null;
  }
}

/**
 * A small per-session cache of the signed-in client's branch documents, keyed `${clientRef}:${section}`.
 * Keyed by the client reference as well as the section, so a member switch (which changes the reference)
 * cannot serve another client's document. Like the section reads, these change only nightly.
 */
const branchSectionCache: Record<string, MiraBranchDocument | null> = {};

/**
 * Reads the signed-in client's branch comparison document (`clients/{client}/mira/branches_{section}`),
 * or `null` where it does not exist. IRES Cloud writes it only where at least two named branches carry a
 * value (so a single-branch client has none, and Datenqualität never does), which is exactly why a null
 * result turns the "Standorte" comparison off: there is nothing to compare. A read error is swallowed the
 * same way, so a permission or network failure hides the comparison rather than failing the screen.
 * Cached for the session.
 */
export async function loadBranchSection(section: MiraSection): Promise<MiraBranchDocument | null> {
  const cacheKey = `${getClientRef()}:${section}`;
  if (cacheKey in branchSectionCache) {
    return branchSectionCache[cacheKey];
  }

  try {
    const snapshot = await db.doc(getClientRef()).collection("mira").doc(branchDocId(section)).get();
    const document = snapshot.exists ? frozenMiraDocument(snapshot.data() as MiraBranchDocument) : null;
    branchSectionCache[cacheKey] = document;
    return document;
  } catch (error) {
    // A branch document we cannot read hides the "Standorte" comparison rather than failing the screen.
    branchSectionCache[cacheKey] = null;
    return null;
  }
}

/** A per-session cache of the signed-in client's per-insurer documents, keyed like the branch cache. */
const insurerSectionCache: Record<string, MiraBranchDocument | null> = {};

/**
 * Reads the signed-in client's per-insurer document (#2610137, 2026-11-03): `insurers_{section}`, or a group
 * master's pooled `group_insurers_{section}`; `null` where IRES Cloud wrote none (fewer than two comparable
 * insurers, Datenqualität, a document before the deploy), which turns the "Versicherer" comparison off. A
 * read error is swallowed the same way. Cached for the session.
 */
export async function loadInsurerSection(section: MiraSection, groupMaster: boolean): Promise<MiraBranchDocument | null> {
  const id = `${groupMaster ? "group_" : ""}insurers_${section}`;
  const cacheKey = `${getClientRef()}:${id}`;
  if (cacheKey in insurerSectionCache) {
    return insurerSectionCache[cacheKey];
  }

  try {
    const snapshot = await db.doc(getClientRef()).collection("mira").doc(id).get();
    const document = snapshot.exists ? frozenMiraDocument(snapshot.data() as MiraBranchDocument) : null;
    insurerSectionCache[cacheKey] = document;
    return document;
  } catch (error) {
    // A per-insurer document we cannot read hides the "Versicherer" comparison rather than failing the screen.
    insurerSectionCache[cacheKey] = null;
    return null;
  }
}

/** A per-session cache of the signed-in member's group view documents, keyed like the branch cache. */
const groupViewSectionCache: Record<string, MiraSectionDocument | null> = {};

/**
 * Reads the signed-in client's view of its group (`clients/{client}/mira/groupView_{section}`, #2610112
 * X6d), or `null` where there is none. IRES Cloud writes it for every member of a group, from the group's
 * own figures without any member id, so a member reads only its own documents. A null result (no group, or
 * a master, which compares its members instead) keeps the "Gruppe" comparison off, and a read error is
 * swallowed the same way. Cached for the session.
 */
export async function loadGroupViewSection(section: MiraSection): Promise<MiraSectionDocument | null> {
  const cacheKey = `${getClientRef()}:${section}`;
  if (cacheKey in groupViewSectionCache) {
    return groupViewSectionCache[cacheKey];
  }

  try {
    const snapshot = await db.doc(getClientRef()).collection("mira").doc(`groupView_${section}`).get();
    const document = snapshot.exists ? frozenMiraDocument(snapshot.data() as MiraSectionDocument) : null;
    groupViewSectionCache[cacheKey] = document;
    return document;
  } catch (error) {
    // A group view we cannot read hides the "Gruppe" comparison rather than failing the screen.
    groupViewSectionCache[cacheKey] = null;
    return null;
  }
}
